import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useIsFocused } from "@react-navigation/native";

import { BackButtonBackHome } from "../Universal/Buttons/BackButtonBackHomeModified";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { formUnits } from "../HomeScreen/HomeScreen";
import { UnitProps } from "../HomeScreen/Components/Unit";
import { webFont } from "../Universal/API/API";
import NothingFound from "./NothingFound/NothingFound";
import SimpleCenteredSpinner from "../Universal/SimpleCenteredSpinner/SimpleCenteredSpinner";
import { ListOfUnitsV3 } from "../HomeScreen/Components/V3";

interface SearchPageProps {
  route: any;
  navigation: any;
}

const createStyles = (scaleAll: number, isSmallVersion: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: "#FFFFFF",
    },
    headerWrap: {
      width: "100%",
      alignItems: "center",
      paddingTop: 0,
      paddingBottom: 18 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
    },
    shell: {
      width: "100%",
      maxWidth: 1480 * scaleAll,
      alignSelf: "center",
      paddingLeft: 4 * scaleAll,
      paddingRight: 4 * scaleAll,
    },
    hero: {
      marginTop: 8 * scaleAll,
      paddingHorizontal: 0,
    },
    eyebrow: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#6B665F",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.7,
      marginBottom: 8 * scaleAll,
    },
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 22 * scaleAll,
      lineHeight: 27 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 8 * scaleAll,
      maxWidth: 980 * scaleAll,
    },
    subtitle: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      lineHeight: 21 * scaleAll,
      color: "#6F6A63",
      fontWeight: "500",
      maxWidth: 980 * scaleAll,
    },
    controlsRow: {
      marginTop: 14 * scaleAll,
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 10 * scaleAll,
      alignItems: "center",
    },
    queryChip: {
      minHeight: 42 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 7 * scaleAll,
      backgroundColor: "#111111",
      justifyContent: "center",
    },
    queryChipText: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#FFFFFF",
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    countChip: {
      minHeight: 42 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 7 * scaleAll,
      backgroundColor: "#EAF4FF",
      borderWidth: 1,
      borderColor: "#CFE5FF",
      justifyContent: "center",
    },
    countChipText: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#0B5CAD",
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    loadingWrap: {
      minHeight: 260 * scaleAll,
      alignItems: "center",
      justifyContent: "center",
    },
  });

const SearchPage: React.FC<SearchPageProps> = ({ route, navigation }) => {
  const query = String(route.params?.query ?? "").trim();
  const { SearcherUnit, isSmallVersion, scaleAll } = useAppContext();
  const styles = useMemo(() => createStyles(scaleAll, isSmallVersion), [isSmallVersion, scaleAll]);

  const [units, setUnits] = useState<UnitProps["unit"][] | null>(null);

  useEffect(() => {
    if (!SearcherUnit) {
      return;
    }

    const data = SearcherUnit.search(query, { returnMatchData: true, threshold: 0.65 });
    let matchedUnits = data.map((d: any) => ({
      ...d.item,
      exactMatch: d.item.art == query || d.item.id == query,
    }));

    const exactMatch = matchedUnits.find((x: any) => x.exactMatch);
    if (exactMatch) {
      matchedUnits = [exactMatch];
    }

    formUnits({
      result: matchedUnits,
      setUnits,
    });
  }, [SearcherUnit, query]);

  const foundCount = units?.length ?? 0;

  const renderHeader = useMemo(
    () => (
      <View style={styles.headerWrap}>
        <View style={styles.shell}>
          <View style={styles.hero}>
            <Text selectable={false} style={styles.eyebrow}>
              Поиск
            </Text>
            <Text selectable={false} style={styles.title}>
              Результаты поиска
            </Text>
            <Text selectable={false} style={styles.subtitle}>
              {foundCount > 0
                ? "Найдены позиции по вашему запросу. Откройте товар или уточните формулировку в строке поиска."
                : "Мы не нашли подходящих позиций. Попробуйте артикул, название коллекции или более короткий запрос."}
            </Text>

            <View style={styles.controlsRow}>
              <View style={styles.queryChip}>
                <Text selectable={false} style={styles.queryChipText}>
                  {`Запрос: ${query || "—"}`}
                </Text>
              </View>

              {units ? (
                <View style={styles.countChip}>
                  <Text selectable={false} style={styles.countChipText}>
                    {`Найдено ${foundCount}`}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>
        </View>
      </View>
    ),
    [foundCount, navigation, query, styles, units]
  );

  const emptyState =
    units && units.length === 0 ? (
      <NothingFound query={query} />
    ) : (
      <View style={styles.loadingWrap}>
        <SimpleCenteredSpinner scaleAll={1} height={30} width={30} />
      </View>
    );

  const isFocused = useIsFocused();

  return (
    <View style={styles.screen}>
      <BackButtonBackHome navigation={navigation} />

      {isFocused ? (
        <ListOfUnitsV3
          navigation={navigation}
          listOfUnis={units ?? []}
          renderHeader={renderHeader}
          listEmptyComponent={emptyState}
        />
      ) : null}
    </View>
  );
};

export default React.memo(SearchPage);
